import { t } from "i18next";
import { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Divider } from "@mui/material";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import { CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import BookingApi from "../../API/Apis/BookingApi";
import { formatTheme } from "../../config/MuiColorThemes";
import BookingGetters from "../../store/onlineBooking/getters";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useBackgroundLoader } from "../../components/UI/BgReservedArea/BackgroundLoader";

export default function UpdatePassword() {
  const navigate = useNavigate();
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);

  const [updateDetails, setUpdateDetails] = useState({
    email: "",
    verificationCode: "",
    newPassword: "",
  });
  const [errorToast, setErrorToast] = useState({
    open: false,
    message: "",
    type: "",
  });

  const [errors, setErrors] = useState({
    verificationCode: "",
    email: "",
    newPassword: "",
  });
  const [changePasswordText, setChangePasswordText] = useState<boolean>(false);

  const appSettings = BookingGetters.getAppSettings();
  const reservedAreaBgImageUrl = appSettings?.config?.reservedAreaBgImage;
  const defaultImage = "/assets/images/reserve_area_car.jpg";
  const { getImage, backgroundStyle } = useBackgroundLoader({
    imageUrl: reservedAreaBgImageUrl,
    defaultImage,
  });

  const validateField = (field: string, value: string): void => {
    let error = "";

    if (value.trim()) {
      if (field === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) error = t("Enter valid email");
      }
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleChange = (e: { target: { id: string; value: string } }): void => {
    const { id, value } = e.target;
    setUpdateDetails((prev) => ({ ...prev, [id]: value }));
    validateField(id, value);
  };

  const handleSubmit = (e: { preventDefault: () => void }): void => {
    e.preventDefault();

    const { verificationCode, email, newPassword } = updateDetails;

    validateField("verification Code", verificationCode);
    validateField("email", email);
    validateField("newPassword", newPassword);

    const localErrors: any = {};

    if (!verificationCode.trim()) {
      localErrors.verificationCode = t("Verification Code is required");
    }

    if (!email.trim()) {
      localErrors.email = t("Email is required");
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        localErrors.email = t("Enter valid email");
      }
    }

    if (!newPassword.trim()) {
      localErrors.newPassword = t("Password is required");
    } else if (newPassword.length < 6) {
      localErrors.newPassword = t("Minimum 6 characters required");
    }

    // If any errors → update state + stop submit
    if (Object.keys(localErrors).length > 0) {
      setErrors((prev: any) => ({ ...prev, ...localErrors }));
      return;
    }

    BookingApi.forgotPassword(updateDetails)
      .then((e) => {
        if (e.status == "success") {
          setChangePasswordText(true);
          setTimeout(() => {
            setChangePasswordText(false);
            navigate("/signin");
          }, 2000);
        }
        if (e.status == "failed") {
          setErrorToast({ open: true, message: e.errorMessage, type: "error" });
        }
        return e.data;
      })
      .catch((e) => console.log(e));
  };

  return (
    <ThemeProvider theme={dynamicTheme}>
      <div className="relative min-h-screen flex items-center justify-center bg-transparent">
        {/* Backgrounds mobile  */}
        {getImage && (
          <div
            style={backgroundStyle}
            className="absolute inset-0 w-full h-full object-cover lg:hidden z-0"
          />
        )}
        <div className="absolute inset-0 md:hidden z-0" />
        <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-screen">
          {/* LEFT IMAGE */}
          <div className="hidden lg:block w-full h-screen overflow-hidden">
            {!getImage ? (
              <div className="absolute top-0 left-0 w-1/2 h-full flex items-center justify-center z-10">
                <CircularProgress sx={{ color: "gray" }} />
              </div>
            ) : (
              <div className="w-full h-full object-cover">
                <div
                  style={backgroundStyle}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>
          {/* Right Login Section */}
          <div className="relative z-10 flex justify-center items-center max-h-screen lg:bg-gray-100">
            <div className="bg-white shadow-lg rounded-2xl px-12 py-11 w-full max-w-[22rem] md:max-w-[25rem] mx-4 border-2">
              {changePasswordText && (
                <div className="flex justify-center mb-4">
                  <CheckIcon
                    sx={{
                      color: dynamicTheme?.palette?.primary?.main,
                      width: "100px",
                      height: "100px",
                    }}
                  />
                </div>
              )}

              <h1
                className="text-center text-2xl font-semibold mb-6"
                style={{ color: dynamicTheme?.palette?.primary?.main }}
              >
                {t("Change Password")}
              </h1>

              <div
                className={`${
                  changePasswordText ? "text-center" : "text-left"
                } text-[#000000] mt-4`}
              >
                {changePasswordText
                  ? t("Password updated successfully")
                  : t("You can change your password here")}
              </div>

              {/* FORM */}
              {!changePasswordText && (
                <Container component="main" disableGutters>
                  <Grid container justifyContent="center">
                    <Box
                      component="form"
                      noValidate
                      onSubmit={handleSubmit}
                      sx={{
                        width: "100%",
                        "& .MuiTextField-root": {
                          mt: 2,
                          width: "100%",
                          backgroundColor: "white",
                        },
                      }}
                    >
                      <TextField
                        required
                        fullWidth
                        id="verificationCode"
                        label={t("Verification Code")}
                        value={updateDetails.verificationCode}
                        onChange={handleChange}
                        error={Boolean(errors.verificationCode)}
                        helperText={
                          errors.verificationCode && (
                            <span style={{ color: "red" }}>
                              {errors.verificationCode}
                            </span>
                          )
                        }
                        FormHelperTextProps={{
                          sx: {
                            textAlign: "left",
                            ml: 0,
                          },
                        }}
                      />

                      <TextField
                        required
                        fullWidth
                        id="email"
                        label={t("Email_Address")}
                        value={updateDetails.email}
                        onChange={handleChange}
                        error={Boolean(errors.email)}
                        helperText={
                          errors.email && (
                            <span style={{ color: "red" }}>{errors.email}</span>
                          )
                        }
                        FormHelperTextProps={{
                          sx: {
                            textAlign: "left",
                            ml: 0,
                          },
                        }}
                      />

                      <TextField
                        required
                        fullWidth
                        id="newPassword"
                        label={t("New Password")}
                        type="password"
                        value={updateDetails.newPassword}
                        onChange={handleChange}
                        error={Boolean(errors.newPassword)}
                        helperText={
                          errors.newPassword && (
                            <span style={{ color: "red" }}>
                              {errors.newPassword}
                            </span>
                          )
                        }
                        FormHelperTextProps={{
                          sx: {
                            textAlign: "left",
                            ml: 0,
                          },
                        }}
                      />

                      <Divider sx={{ bgcolor: "grey.300", mt: 2, mb: 4 }} />

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{
                          mb: 2,
                          fontWeight: "bold",
                          fontSize: "17px",
                          p: 1,
                          textTransform: "none",
                        }}
                      >
                        {t("Update Password")}
                      </Button>
                    </Box>
                  </Grid>
                </Container>
              )}
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={errorToast.open}
        autoHideDuration={4000}
        onClose={() => setErrorToast({ ...errorToast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        ContentProps={{
          sx: {
            backgroundColor: "#d32f2f",
            color: "white",
            fontWeight: "bold",
            fontSize: "14px",
          },
        }}
        message={errorToast.message}
      />
    </ThemeProvider>
  );
}
